/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-empty-function */
import React from 'react';
import { Box, Divider, IconButton, PaginationProps, Stack, TablePagination, Typography } from '@mui/material';
import usePagination, { UsePaginationProps } from '@mui/material/usePagination';
import { Icon } from '@iconify/react';
import Select from '../Select';

interface Props {
  paginationProps?: Omit<UsePaginationProps, 'count'>;
  count: number;
  countOpts?: number[];
  showPageToggles?: boolean;
  page: number;
  itemsPerPage: number;
  showJumpToFirst?: boolean;
  showJumToLast?: boolean;
  onPageChange: (pageNum: number) => void;
  onItemsPerPageChange: (n: number) => void;
}

interface MoveBtnProps {
  disabled?: boolean;
  onClick: React.ReactEventHandler;
}

const PrevBtn: React.FC<MoveBtnProps> = ({ disabled = false, onClick }) => {
  return (
    <IconButton sx={{ borderRadius: 2, p: 0.5, gap: 0.5 }} disabled={disabled} onClick={onClick}>
      <Icon icon="ant-design:left-outlined" fontSize={16} />
      <Typography variant="caption">Previous</Typography>
    </IconButton>
  );
};

const NextBtn: React.FC<MoveBtnProps> = ({ disabled = false, onClick }) => {
  return (
    <IconButton sx={{ borderRadius: 2, p: 0.5, gap: 0.5 }} disabled={disabled} onClick={onClick}>
      <Typography variant="caption">Next</Typography>
      <Icon icon="ant-design:right-outlined" fontSize={16} />
    </IconButton>
  );
};

const Pagination: React.FC<Props> = ({
  paginationProps,
  count,
  countOpts = [10],
  showPageToggles = false,
  page,
  showJumpToFirst = true,
  showJumToLast = true,
  ...rest
}) => {
  const totalPages = Math.ceil(count / rest.itemsPerPage);
  const { items } = usePagination({ ...paginationProps, page, count: totalPages });

  const options = countOpts.map((opt) => ({ label: String(opt), value: opt }));

  const renderPaginationBtn = () => {
    if (!showPageToggles) {
      return (
        <>
          <PrevBtn
            disabled={page === 1}
            onClick={() => {
              rest.onPageChange(page - 1 || 1);
            }}
          />
          <Typography variant="caption" alignSelf="center" px={0.5}>
            {page} of {totalPages}
          </Typography>
          <NextBtn disabled={page === totalPages} onClick={() => rest.onPageChange(page + 1)} />
        </>
      );
    }

    return items.map(({ page: itemPage, type, selected, ...item }, index) => {
      if (type === 'start-ellipsis' || type === 'end-ellipsis') {
        return (
          <Typography variant="caption" alignSelf="center" key={index}>
            ...
          </Typography>
        );
      }
      if (type === 'previous') {
        return <PrevBtn {...item} onClick={() => rest.onPageChange(page - 1)} key={index} />;
      }
      if (type === 'next') {
        return <NextBtn disabled={item.disabled} onClick={() => rest.onPageChange(page + 1)} key={index} />;
      }
      return (
        <IconButton
          color={selected ? 'primary' : 'default'}
          size="small"
          sx={{ borderRadius: 2, p: 1 }}
          disabled={item.disabled}
          onClick={() => rest.onPageChange(itemPage || page)}
          key={index}
        >
          <Typography variant="caption">{itemPage}</Typography>
        </IconButton>
      );
    });
  };

  const renderPagination = () => {
    return (
      <Stack direction="row" spacing={0.5}>
        {showJumpToFirst && (
          <>
            <IconButton sx={{ borderRadius: 2, p: 0.5, gap: 0.5 }} onClick={() => rest.onPageChange(1)}>
              <Icon icon="ant-design:double-left-outlined" fontSize={16} />
              <Typography variant="caption">First</Typography>
            </IconButton>
            <Divider orientation="vertical" sx={{ height: 20, alignSelf: 'center' }} />
          </>
        )}
        {renderPaginationBtn()}
        {showJumToLast && (
          <>
            <Divider orientation="vertical" sx={{ height: 20, alignSelf: 'center' }} />
            <IconButton sx={{ borderRadius: 2, p: 0.5, gap: 0.5 }} onClick={() => rest.onPageChange(totalPages)}>
              <Typography variant="caption">Last</Typography>
              <Icon icon="ant-design:double-right-outlined" fontSize={16} />
            </IconButton>
          </>
        )}
      </Stack>
    );
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="caption" onClick={() => rest.onItemsPerPageChange(30)}>
          View
        </Typography>
        <Box sx={{ maxWidth: 80 }}>
          <Select<number>
            options={options}
            selectedOption={String(rest.itemsPerPage)}
            setSelectedOption={(opt) => rest.onItemsPerPageChange(opt)}
          />
        </Box>
        <Typography variant="caption">Items Per Page</Typography>
      </Stack>
      {renderPagination()}
    </Box>
  );
};

export default Pagination;
